import { useState, useCallback, useMemo } from 'react';
import { UsersFilter } from '../api';
import { useDidUpdateEffect } from '@/hooks/useDidUpdateEffect';

export interface UserFiltersConfig {
  initialSearch?: string;
  initialOrganizationId?: number;
  initialRoles?: string[];
  initialSortBy?: string;
  initialSortOrder?: string;
  initialPage?: number;
  initialPerPage?: number;
}

export const useUserFilters = (config: UserFiltersConfig = {}) => {
  const {
    initialSearch = '',
    initialOrganizationId,
    initialRoles = [],
    initialSortBy = 'created_at',
    initialSortOrder = 'desc',
    initialPage = 1,
    initialPerPage = 50,
  } = config;

  // Filter states
  const [search, setSearch] = useState(initialSearch);
  const [organizationId, setOrganizationId] = useState<number | undefined>(initialOrganizationId);
  const [roles, setRoles] = useState<string[]>(initialRoles);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [page, setPage] = useState(initialPage);
  const [perPage, setPerPage] = useState(initialPerPage);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearch('');
    setOrganizationId(undefined);
    setRoles([]);
    setSortBy('created_at');
    setSortOrder('desc');
    setPage(1);
  }, []);

  // Page size change handler
  const handlePageSizeChange = useCallback((newPerPage: number) => {
    setPerPage(newPerPage);
    setPage((prev) => (prev === 1 ? prev : 1));
  }, []);

  useDidUpdateEffect(() => {
    setPage((prev) => (prev === 1 ? prev : 1));
  }, [search, organizationId, roles, sortBy, sortOrder]);

  // Memoized filter object for API calls
  const filters = useMemo((): UsersFilter => {
    const result: UsersFilter = {
      page,
      per_page: perPage,
      sort_by: sortBy as any,
      sort_order: sortOrder as any,
    };

    if (search.trim()) result.search = search.trim();
    if (organizationId) result.organization_id = organizationId;
    if (roles.length > 0) result['roles[]'] = roles;

    return result;
  }, [search, organizationId, roles, sortBy, sortOrder, page, perPage]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(search.trim() || organizationId || roles.length > 0);
  }, [search, organizationId, roles]);

  return {
    // Filter values
    search,
    organizationId,
    roles,
    sortBy,
    sortOrder,
    page,
    perPage,
    
    // Filter setters
    setSearch,
    setOrganizationId,
    setRoles,
    setSortBy,
    setSortOrder,
    setPage,
    setPerPage: handlePageSizeChange,
    
    // Utility methods
    clearFilters,
    
    // Computed values
    filters,
    hasActiveFilters,
  };
}; 
